import { useCallback, useEffect, useState } from 'react'

import s from './Invite.module.css'

/**
 * Your own link to hand out.
 *
 * Anybody with an account has one, not just the curator: the calendar is worth more the more
 * people are on it, and asking one person to be the gate on every friendship is how a
 * private site stays at twelve people for ever.
 *
 * **It is weaker than a one-off invitation, deliberately.** Anyone holding it can join. What
 * keeps that from being the shared code all over again is that it belongs to *you* — a leak
 * is contained to your link, rotating costs a paste rather than locking anybody else out,
 * and the list below says who came in on it.
 */

type Mine = { link: string; left: number; of: number; joined: string[] }

export function Invite() {
  const [mine, setMine] = useState<Mine | null>(null)
  const [copied, setCopied] = useState(false)
  const [trouble, setTrouble] = useState('')

  const ask = useCallback((rotate: boolean) => {
    setTrouble('')
    /*
     * A POST even on load, because `rotate` mints when there is nothing to read and minting
     * is a write. A GET that quietly created a credential would make one for every
     * speculative fetch a browser felt like.
     */
    fetch('/invitation', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rotate }),
    })
      .then(answer => (answer.ok ? answer.json() : Promise.reject(new Error('no'))))
      .then((found: Mine) => {
        setMine(found)
        setCopied(false)
      })
      .catch(() => setTrouble('that did not work'))
  }, [])

  useEffect(() => ask(false), [ask])

  const whole = mine ? `${window.location.origin}${mine.link}` : ''

  function copy() {
    // `writeText` needs a secure context, which a phone on the local network is not. The
    // link is on the page either way, so failing means "select it yourself" rather than
    // nothing at all.
    void navigator.clipboard
      ?.writeText(whole)
      .then(() => setCopied(true))
      .catch(() => setTrouble('copy it from the box above'))
  }

  return (
    <main className={s.page}>
      <h1 className={s.title}>Invite a friend</h1>
      <p className={s.blurb}>
        Send them this link. Anyone who opens it can sign in with Google and join.
      </p>

      <p className={s.err}>{trouble}</p>

      <div className={s.linkRow}>
        <input className={s.link} value={whole} readOnly aria-label="Your invite link" />
        <button type="button" className={s.copy} onClick={copy} disabled={!mine}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {mine ? (
        <p className={s.count}>
          {mine.left} of {mine.of} left ·{' '}
          <button type="button" className={s.plain} onClick={() => ask(true)}>
            Get a new link
          </button>
        </p>
      ) : null}

      {/*
        * Drawn even when it is empty, which is the whole reason it is here: a section that
        * appears only once somebody has joined leaves the page with one input on it and no
        * way to tell whether that is all of it. "Nobody yet" is the state, not filler.
        */}
      {mine ? (
        <section className={s.joined}>
          <h2 className={s.subtitle}>Joined on your link</h2>
          {mine.joined.length ? (
            <ul>
              {mine.joined.map(name => (
                <li key={name}>
                  <a href={`/u/${name}`}>{name}</a>
                </li>
              ))}
            </ul>
          ) : (
            <p className={s.nobody}>Nobody yet.</p>
          )}
        </section>
      ) : null}
    </main>
  )
}
